import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPinIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Eye, FileText, Cookie, HelpCircle, Phone } from 'lucide-react';
import { CONTACT } from '../../config/contact';

// Shared public footer. Extracted from Pricing.js so every public page
// (Pricing, FAQ, …) renders an identical footer.
const PublicFooter = () => {
  const navigate = useNavigate();

  return (
    <footer className="bg-gray-950 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJuIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjciIG51bU9jdGF2ZXM9IjEwIiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI24pIiBvcGFjaXR5PSIuMSIvPjwvc3ZnPg==')] opacity-5 mix-blend-soft-light pointer-events-none"></div>
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 text-center md:text-left">
          {/* Address Column */}
          <div className="space-y-4 flex flex-col items-center md:items-start">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Address</h4>
            <div className="flex items-start justify-center md:justify-start gap-3 text-sm text-gray-400 group">
              <MapPinIcon className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5 group-hover:text-teal-400 transition-colors" />
              <span className="group-hover:text-gray-300 transition-colors">
                OSPITO SYSTEMS (U) LTD<br />
                Plot 34 Namasagali Road, Kamuli<br />
                P.O Box 130555<br />
                Kamuli, Uganda
              </span>
            </div>
          </div>

          {/* Contact Column */}
          <div className="space-y-4 flex flex-col items-center md:items-start">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a href={CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center md:justify-start gap-3 text-sm text-gray-400 hover:text-white group transition-colors">
                  <div className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                    <svg className="h-4 w-4 text-teal-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                  </div>
                  <span>{CONTACT.whatsappDisplay}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${CONTACT.phoneTel}`} className="flex items-center justify-center md:justify-start gap-3 text-sm text-gray-400 hover:text-white group transition-colors">
                  <div className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                    <Phone className="h-4 w-4 text-teal-500" />
                  </div>
                  <span>{CONTACT.phoneDisplay}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Email Column */}
          <div className="space-y-4 flex flex-col items-center md:items-start">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Email</h4>
            <ul className="space-y-3">
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center justify-center md:justify-start gap-3 text-sm text-gray-400 hover:text-white group transition-colors">
                  <div className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                    <EnvelopeIcon className="h-4 w-4 text-teal-500" />
                  </div>
                  <span>{CONTACT.email}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.supportEmail}`} className="flex items-center justify-center md:justify-start gap-3 text-sm text-gray-400 hover:text-white group transition-colors">
                  <div className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                    <EnvelopeIcon className="h-4 w-4 text-teal-500" />
                  </div>
                  <span>{CONTACT.supportEmail}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Quick Links Column */}
          <div className="space-y-4 flex flex-col items-center md:items-start">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Legal</h4>
            <ul className="flex flex-col items-center md:items-start space-y-2 text-sm text-gray-400">
              <li>
                <button onClick={() => navigate('/?legal=privacy')} className="hover:text-teal-400 transition-colors flex items-center justify-center md:justify-start gap-2 group cursor-pointer">
                  <Eye className="w-3.5 h-3.5 text-gray-500 group-hover:text-teal-400" />
                  Privacy Policy
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/?legal=terms')} className="hover:text-teal-400 transition-colors flex items-center justify-center md:justify-start gap-2 group cursor-pointer">
                  <FileText className="w-3.5 h-3.5 text-gray-500 group-hover:text-teal-400" />
                  Terms of Service
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/?legal=cookies')} className="hover:text-teal-400 transition-colors flex items-center justify-center md:justify-start gap-2 group cursor-pointer">
                  <Cookie className="w-3.5 h-3.5 text-gray-500 group-hover:text-teal-400" />
                  Cookie Policy
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-center items-center gap-4">
          <p className="text-sm text-gray-500 text-center">
            &copy; 2026 Ospito+ HMS. All rights reserved | Designed & Maintained by OSPITO SYSTEMS (U) LTD
          </p>
          <Link
            to="/faq"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-teal-400 transition-colors group"
          >
            <HelpCircle className="w-3.5 h-3.5 text-gray-500 group-hover:text-teal-400" />
            FAQ
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
